import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { api, WHATSAPP_NUMBER } from '../lib/api';
import { toast } from 'sonner';
import { KeyRound, MessageCircle, CheckCircle2 } from 'lucide-react';

export default function ForgotPin() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: verify, 2: set new PIN, 3: success
  const [verify, setVerify] = useState({ phone: '', baby_birth_date: '' });
  const [resetToken, setResetToken] = useState('');
  const [newForm, setNewForm] = useState({ pin: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const doVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post('/mothers/forgot-pin/verify', {
        phone: verify.phone.trim(),
        baby_birth_date: verify.baby_birth_date,
      }).then((r) => r.data);
      setResetToken(res.reset_token);
      setStep(2);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string'
        ? msg
        : (lang === 'zh'
          ? '找不到符合的个人档案。请确认 WhatsApp 电话和宝宝生日是否正确，或 WhatsApp 联系 Soo 协助。'
          : 'We could not find a matching profile. Please check your WhatsApp number and baby birth date, or WhatsApp Soo for help.'));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(newForm.pin)) {
      toast.error(lang === 'zh' ? 'PIN 必须是 4-6 位数字' : 'PIN must be 4–6 digits');
      return;
    }
    if (newForm.pin !== newForm.confirm) {
      toast.error(lang === 'zh' ? '两次输入的 PIN 不一致' : 'The two PINs do not match');
      return;
    }
    setBusy(true);
    try {
      await api.post('/mothers/forgot-pin/reset', {
        reset_token: resetToken,
        new_pin: newForm.pin,
      });
      setStep(3);
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (lang === 'zh' ? '更新失败' : 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-app py-8 sm:py-12 max-w-lg animate-fade-in-up" data-testid="forgot-pin-page">
      <div className="card-base p-6 sm:p-10">
        <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-4">
          <KeyRound size={20} className="text-terracotta" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-2">
          {lang === 'zh' ? '重设 PIN' : 'Reset PIN'}
        </h1>

        {step === 1 && (
          <>
            <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
              {lang === 'zh'
                ? '请输入注册时使用的 WhatsApp 电话和宝宝生日来确认身份。'
                : 'Enter the WhatsApp number and baby birth date you used during registration.'}
            </p>
            <form onSubmit={doVerify} className="space-y-4" data-testid="forgot-pin-step1">
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_phone')}</span>
                <input
                  type="tel"
                  value={verify.phone}
                  onChange={(e) => setVerify((p) => ({ ...p, phone: e.target.value }))}
                  placeholder={t('profile_phone_hint')}
                  required
                  data-testid="forgot-phone-input"
                  className="input-base"
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_baby_dob')}</span>
                <input
                  type="date"
                  value={verify.baby_birth_date}
                  onChange={(e) => setVerify((p) => ({ ...p, baby_birth_date: e.target.value }))}
                  required
                  data-testid="forgot-dob-input"
                  className="input-base"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-verify-btn"
                className="btn-primary w-full disabled:opacity-60"
              >
                {busy ? '…' : (lang === 'zh' ? '下一步' : 'Continue')}
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noreferrer"
                className="block text-center text-xs text-ink-tertiary hover:text-terracotta pt-1"
                data-testid="forgot-whatsapp-help"
              >
                <MessageCircle size={11} className="inline mr-1" />
                {lang === 'zh' ? 'WhatsApp 联系 Soo 协助' : 'WhatsApp Soo for help'}
              </a>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
              {lang === 'zh' ? '请设置新的 4-6 位数字 PIN。' : 'Set a new 4–6 digit PIN.'}
            </p>
            <form onSubmit={doReset} className="space-y-4" data-testid="forgot-pin-step2">
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                  {lang === 'zh' ? '新 PIN' : 'New PIN'}
                </span>
                <input
                  type="password"
                  inputMode="numeric"
                  value={newForm.pin}
                  onChange={(e) => setNewForm((p) => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  required
                  data-testid="forgot-new-pin-input"
                  className="input-base"
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">
                  {lang === 'zh' ? '确认新 PIN' : 'Confirm new PIN'}
                </span>
                <input
                  type="password"
                  inputMode="numeric"
                  value={newForm.confirm}
                  onChange={(e) => setNewForm((p) => ({ ...p, confirm: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  required
                  data-testid="forgot-confirm-pin-input"
                  className="input-base"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-reset-btn"
                className="btn-primary w-full disabled:opacity-60"
              >
                {busy ? '…' : (lang === 'zh' ? '更新 PIN' : 'Update PIN')}
              </button>
            </form>
          </>
        )}

        {step === 3 && (
          <div data-testid="forgot-pin-success" className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-sage-light flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={26} className="text-sage" />
            </div>
            <p className="text-sm text-ink-secondary leading-relaxed mb-6">
              {lang === 'zh'
                ? 'PIN 已更新，你现在可以使用 WhatsApp 电话和新的 PIN 登入。'
                : 'Your PIN has been updated. You can now log in using your WhatsApp number and new PIN.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/profile/login', { replace: true })}
              data-testid="forgot-go-login-btn"
              className="btn-primary w-full"
            >
              {lang === 'zh' ? '前往登入' : 'Go to Log in'}
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-ink-tertiary">
          <Link to="/profile/login" className="hover:text-terracotta" data-testid="forgot-back-link">
            ← {lang === 'zh' ? '返回登入' : 'Back to login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
